import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { find, findIndex, includes, map, reduce, set } from 'lodash';
import { Observable } from 'rxjs';
import { ImStudent } from '../../../shared/services/im-models/im-student.service';
import { ImUser } from '../../../shared/services/im-models/im-user';
import { IStudent } from '../../../shared/typings/interfaces/student.interface';
import { IUser, IUserMini, IUserMiniWithRole } from '../../../shared/typings/interfaces/user.interface';
import { StudentPanelService } from '../student-panel.service';
import { DIM1_MADLIB_PATHS } from './../../../../app/routes/main/route-resolvers/mad-lib-options-resolver';
import { PointPeopleService } from './../../../shared/services/point-people/point-people.service';
import { LoadMadlibOptions } from './../../../store/actions/madlib-options-actions';
import { getUsersEntities } from './../../../store/selectors/users-selectors';
import PointPeopleConfig from './student-point-people-panel.config';

type IUserOption = {
  firstName: string;
  lastName: string;
  name: string;
  _id: string;
  email: string;
};

type IUserMenuOption = {
  removeUser?: boolean;
  name?: string;
  user?: IUserMiniWithRole;
};

type ITableRow = {
  field: string;
  name: string | number;
  highlight?: string;
  editable?: boolean;
  options?: IUserMenuOption[];
  display?: boolean;
  subRows?: string[];
};

type IPointPerson = {
  type: string;
  user: IUserMini;
};

const ppTypeMap = {
  Academic: 'ACADEMIC',
  Attendance: 'ATTENDANCE',
  Postsecondary: 'POSTSECONDARY',
  Credits: 'CREDITS',
  Regents: 'REGENTS',
  'Guidance Counselor': 'GUIDANCE_COUNSELOR',
  Advisor: 'ADVISOR',
};

@Component({
  selector: 'student-point-people-panel',
  template: require('./student-point-people-panel.component.html'),
  styles: [require('./student-point-people-panel.scss')],
})
export class StudentPointPeoplePanel implements OnInit {
  @Input() student: IStudent;
  @Input() collapsed: boolean;
  @Input() currentUser: IUser;
  @Output() saveStudent: EventEmitter<any> = new EventEmitter();

  panelTitle: string;
  flagsTable: ITableRow[];
  otherPointPeopleTable: ITableRow[];
  userCanEdit: boolean;
  parsedTeacherNames: string;
  userIsViewOnly: boolean;
  userEntities$: Observable<IUserMiniWithRole[]>;
  userFlagPpOptions: IUserMenuOption[];
  userNonFlagPpOptions: IUserMenuOption[];

  readonly noUserDropdownOption: IUserMenuOption = {
    removeUser: true,
    name: '—',
    user: {
      _id: '',
      name: {
        firstName: '',
        lastName: '',
      },
      gafeEmail: '',
      doeEmail: null,
      nvRole: { type: 'school_admin' },
      delegatedRole: null,
    },
  };

  constructor(
    private studentPanel: StudentPanelService,
    private store: Store<any>,
    private imStudent: ImStudent,
    @Inject('sdcActions') private sdcActions,
    private pointPeopleService: PointPeopleService,
    private imUser: ImUser,
  ) {}

  ngOnInit() {
    this.panelTitle = PointPeopleConfig.title;
    this.collapsed = this.studentPanel.getInitialPanelState('student-point-people-panel');

    this.userEntities$ = this.store.select(getUsersEntities);
    this.userEntities$.subscribe((users: IUserMiniWithRole[]) => {
      const schoolUsersThatCanBeFlagPp = this.pointPeopleService.filterForUsersThatCanBeFlagPp(users);
      const schoolUsersThatCanBeNonFlagPp = this.pointPeopleService.filterForUsersThatCanBeNonFlagPp(users);

      this.userFlagPpOptions = this.getFormattedUserDropdownOptions(schoolUsersThatCanBeFlagPp);
      this.userNonFlagPpOptions = this.getFormattedUserDropdownOptions(schoolUsersThatCanBeNonFlagPp);
    });

    this.userCanEdit = this.imUser.isEditingUser(this.currentUser);
    this.userIsViewOnly = this.imUser.isViewType(this.currentUser);

    // build table one based on ES/MS/HS students
    this.generateTables();
  }

  ngOnChanges(changes): void {
    if (changes.student) this.generateTables();
  }

  generateTables() {
    this.flagsTable = this.buildTable1ForStudent(this.student);
    this.otherPointPeopleTable = this.buildTable2ForStudent(this.student);
  }

  togglePanelBody(): void {
    this.collapsed = this.studentPanel.togglePanelBody(this.collapsed, this.panelTitle);
  }

  fullName(person: IPointPerson): string {
    if (person && person.user && person.user.firstName) {
      return `${person.user.firstName} ${person.user.lastName}`;
    }
    return '—';
  }

  buildTable1ForStudent(student: IStudent): ITableRow[] {
    const { isHS } = student;
    const flagPointPeople = this.imStudent.getFlagPointPeople(student);
    const { ATTENDANCE, ACADEMIC, POSTSECONDARY, REGENTS, CREDITS } = flagPointPeople;

    if (isHS) {
      return [
        {
          field: 'Attendance',
          name: this.fullName(ATTENDANCE),
          editable: true,
          options: this.userFlagPpOptions,
        },
        {
          field: 'Academic',
          name: this.fullName(ACADEMIC),
          editable: true,
          options: this.userFlagPpOptions,
        },
        {
          field: 'Credits',
          name: this.fullName(CREDITS),
          editable: true,
          options: this.userFlagPpOptions,
        },
        {
          field: 'Regents',
          name: this.fullName(REGENTS),
          editable: true,
          options: this.userFlagPpOptions,
        },
        {
          field: 'Postsecondary',
          name: this.fullName(POSTSECONDARY),
          editable: true,
          options: this.userFlagPpOptions,
        },
      ];
    } else {
      return [
        {
          field: 'Academic',
          name: this.fullName(ACADEMIC),
          editable: true,
          options: this.userFlagPpOptions,
        },
        {
          field: 'Attendance',
          name: this.fullName(ATTENDANCE),
          editable: true,
          options: this.userFlagPpOptions,
        },
      ];
    }
  }

  buildTable2ForStudent(student: IStudent): ITableRow[] {
    const { isHS, otherStaff } = student;

    const otherPointPeople = this.imStudent.getOtherPointPeople(student);
    const { GUIDANCE_COUNSELOR, ADVISOR } = otherPointPeople;

    const otherStaffTeachers = reduce(
      otherStaff,
      (result, person: IPointPerson) => {
        if (person.type === 'TEACHER') {
          const fullName = this.fullName(person);
          result.push(fullName);
        }
        return result;
      },
      [],
    );
    this.parsedTeacherNames = otherStaffTeachers.length > 0 ? otherStaffTeachers.join(', ') : '—';

    if (isHS) {
      const otherStaffAtsCounselor = find(otherStaff, (person: IPointPerson) => {
        return person.type === 'GUIDANCE_COUNSELOR';
      });

      return [
        {
          field: 'Guidance Counselor',
          name: this.fullName(GUIDANCE_COUNSELOR),
          editable: true,
          options: this.userNonFlagPpOptions,
          display: true,
        },
        {
          field: 'Advisor',
          name: this.fullName(ADVISOR),
          editable: true,
          options: this.userNonFlagPpOptions,
          display: true,
        },
        {
          field: 'ATS Counselor',
          name: this.fullName(otherStaffAtsCounselor),
          editable: false,
          display: !!otherStaffAtsCounselor,
        },
        {
          field: 'Teachers',
          highlight: 'teachers-row',
          name: this.parsedTeacherNames,
          editable: false,
          display: true,
        },
      ];
    } else {
      return [
        {
          field: 'Teachers',
          highlight: 'teachers-row',
          name: this.parsedTeacherNames,
          editable: false,
          display: true,
        },
      ];
    }
  }

  handlePointPersonChange(ppType: string, menuOption: IUserMenuOption) {
    const mappedType = ppTypeMap[ppType];

    const { _id, name, gafeEmail, doeEmail } = menuOption.user;

    const menuOptionUserMini = {
      userId: _id,
      firstName: name.firstName,
      lastName: name.lastName,
      gafeEmail,
      doeEmail,
    };

    const newPP = { type: mappedType, user: menuOptionUserMini };

    const pointPeople = this.student.pointPeople.slice();
    const ppIndex = findIndex(pointPeople, (person: IPointPerson) => {
      return person.type === mappedType;
    });

    // remove point person if option has removeUser property
    if (menuOption.removeUser) {
      pointPeople.splice(ppIndex, 1);
    } else {
      if (ppIndex >= 0) {
        pointPeople.splice(ppIndex, 1, newPP);
      } else {
        // student doesn't have a point person of the selected type (JE)
        pointPeople.push(newPP);
      }
    }

    this.flagsTable = this.buildTable1ForStudent(this.student); // update current view
    this.otherPointPeopleTable = this.buildTable2ForStudent(this.student);
    // make api patch for student's pp array
    const payload = {
      patches: {
        path: 'pointPeople',
        newValue: pointPeople,
        _ids: [this.student._id],
      },
    };
    const path = 'pointPeople';
    const patch = set({}, path, pointPeople);

    // patches backend and updates list based slices on success
    // this.store.dispatch(new BulkUpdateStudents(payload));
    this.saveStudent.emit({ patch, path });
    const madlibPayload = { paths: DIM1_MADLIB_PATHS, schoolId: this.student.schoolId };
    this.store.dispatch(new LoadMadlibOptions(madlibPayload));
    // just updates sdc store. TODO: pass true so method knows to bypass EventBus
    this.sdcActions.recordMultiStudentUpdate(payload);
  }

  isOptionActive(ppType: string, menuOption: IUserMenuOption): boolean {
    const mappedType = ppTypeMap[ppType];
    const activePerson = find(this.student.pointPeople, (person: IPointPerson) => {
      return person.type === mappedType;
    });
    if (menuOption.removeUser) {
      return !activePerson;
    }
    return activePerson ? activePerson.user.userId === menuOption.user._id : false;
  }

  getFormattedUserDropdownOptions(users: IUserMiniWithRole[]): IUserMenuOption[] {
    const options: IUserMenuOption[] = map(users, (user: IUserMiniWithRole) => {
      const firstName = user.preferredName.firstName || user.name.firstName;
      const lastName = user.preferredName.lastName || user.name.lastName;
      return {
        name: `${firstName} ${lastName}`,
        user,
      };
    });

    options.unshift(this.noUserDropdownOption);

    return options;
  }
}
